import type { CustomDomainStatus } from '@/types';

const DOMAIN_PATTERN =
  /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;

export type CustomDomainBadgeVariant =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'destructive'
  | 'success'
  | 'warning'
  | 'info'
  | 'outline';

export function normalizeCustomDomainInput(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/.*$/, '')
    .replace(/\.$/, '');
}

export function validateCustomDomainFormat(value: string): string | null {
  const domain = normalizeCustomDomainInput(value);

  if (!domain) {
    return 'Enter the domain you want for your store.';
  }

  if (domain.includes(' ')) {
    return 'Domains cannot contain spaces.';
  }

  if (!domain.includes('.')) {
    return 'Include an extension such as .com or .ng.';
  }

  if (!DOMAIN_PATTERN.test(domain)) {
    return 'Enter a valid domain like yourbrand.com.';
  }

  return null;
}

export function getSellytStorefrontUrl(subdomain: string): string {
  return `https://${subdomain}.sellyt.shop`;
}

export function getCustomDomainUrl(domain: string): string {
  return `https://${domain}`;
}

interface DomainStatusPresentation {
  label: string;
  description: string;
  badgeVariant: CustomDomainBadgeVariant;
}

const STATUS_PRESENTATION: Record<string, DomainStatusPresentation> = {
  pending_verification: {
    label: 'Pending verification',
    description:
      'Add the DNS records below at your registrar, then verify. This can take a few minutes after you save them.',
    badgeVariant: 'warning',
  },
  active: {
    label: 'Active',
    description:
      'Your custom domain is live and pointing to your Sellyt store.',
    badgeVariant: 'success',
  },
  failed: {
    label: 'Verification failed',
    description:
      'We could not verify your DNS records yet. Check the values and try again.',
    badgeVariant: 'destructive',
  },
};

export function getCustomDomainStatusPresentation(
  status: CustomDomainStatus
): DomainStatusPresentation {
  return (
    STATUS_PRESENTATION[status] ?? {
      label: String(status).replace(/_/g, ' '),
      description: 'Domain status updated by Sellyt.',
      badgeVariant: 'default' as const,
    }
  );
}

export function isCustomDomainActive(status: CustomDomainStatus): boolean {
  return status === 'active';
}

export function needsDnsSetup(status: CustomDomainStatus): boolean {
  return status === 'pending_verification' || status === 'failed';
}
